import React, { useState, useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './ProductFilters.scss';
import { FilterGroup, FilterSection } from "@/components/ConfigurationEditors/FilterGroup";
import { ByzzerBrandSearch } from "@/components/ByzzerBrandSearch";
import { ByzzerCategorySelect } from "@/components/ByzzerCategorySelect";
import { CharacteristicCriteriaBuilder } from "@/components/CharacteristicCriteriaBuilder";
import { ProductRunConfigOptions } from "@/types/RunConfigOptions";
import { DefaultConfigWizardContextValue, DefaultConfigWizardContext } from "../../DefaultsConfigWizard/DefaultsConfigWizardContext";
import { useUser } from "@/contexts/UserContext";
import { useTenantApi } from "@/hooks";
import { ByzzerSelect, useWizardStepContext } from "@byzzer/ui-components";
import { DefaultConfigLabels } from "@/pages/Onboarding/OnboardingSteps";
import { OnboardingStepCode } from "@/pages/Onboarding/OnboardingWizard";
import { OmniCategorySelect } from "@/components/OmniCategorySelect";
import { omniProductLevelOptions } from "@/constants/omniProducts.constants";
import { OmniBrandSearch } from "@/components/OmniBrandSearch/OmniBrandSearch";
import { ByzzerOshCategorySelect } from "@/components/ByzzerOshCategorySelect";
import { ByzzerOshCharacteristicBuilder } from "@/components/ByzzerOshCharacteristicBuilder/ByzzerOshCharacteristicBuilder";
import { CharacteristicCondition } from "@/types/ReportRun";
import { ByzzerOshCharacteristicsDimensionSelect } from "@/components/ByzzerOshCharacteristicsDimensionSelect";
import { ByzzerOshDemographicCriteriaBuilder } from "@/components/ByzzerOshDemographicCriteriaBuilder";
import { SubCategorySelect } from "@/components/SubCategorySelect";
import { SelectorLabelInfo } from "@/components/SelectorLabelInfo";
import { ByzzerOshSubCategorySelect } from "@/components/ByzzerOshSubCategorySelect";

export type ProductFiltersRef = {
    value?: DefaultConfigWizardContextValue['value'];
}

export type ProductFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<DefaultConfigWizardContextValue['value']>) => void;
    value?: DefaultConfigWizardContextValue['value'];
    summary?: ReactNode;
    labelsAndTips?: DefaultConfigLabels;
    code?: OnboardingStepCode;
    sku?: string;
} & Partial<Omit<ProductRunConfigOptions, 'type' | 'title'>> 
  & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export const MESSAGE_NO_PREFERENCE_CATEGORY_MATCH = 'The categories you have selected do not contain any of your focus brands.';

export const ProductFilters = forwardRef<ProductFiltersRef, ProductFiltersProps>((
    {
        className,
        includeCategories,
        includeCharacteristics,
        includeFocusBrands,
        includeOmniCategories,
        includeOmniProducts,
        includeOmniShopperCategories,
        includeOmniShopperSubCategories,
        includeOmniShopperCharacteristics,
        includeOmniShopperProductDimensions,
        includeOmniShopperDemographics,
        maxCharacteristics,
        maxCharacteristicValues,
        selectAllListedValuesEnabled = false,
        maxDemographicConditions,
        maxDemographicValue,
        maxCategories,
        maxSubCategories,
        maxOmniProducts,
        maxFocusBrands,
        name,
        value,
        onChange,
        summary,
        labelsAndTips,
        code,
        sku,
        requireProductSubcategory,
        includeProductSubcategory,
        ...props
    }, ref) => {

    const baseClassName = 'byz-product-filters';

    const [internalValue, setInternalValue] = useState<DefaultConfigWizardContextValue['value']>({});

    const { defaultRunConfig } = useUser();
    const { getCategoriesByBrands } = useTenantApi();

    const { value: contextValue, onChange: onContextChange, state: contextState, selectedDataset } = useContext(
        DefaultConfigWizardContext
    );

    const { showMessage, hideMessage } = useWizardStepContext();

    const [categoriesToCheckForIntersect, setCategoriesToCheckForIntersect] = useState<string[]>([]);

    useEffect(() => {
        (async () => {
            const { brands } = internalValue;
            const categories = brands?.length ? await getCategoriesByBrands(brands) : [];
            setCategoriesToCheckForIntersect(categories.flat());
        })();
    }, [internalValue?.brands]);

    function handleChange(e: ByzzerChangeEvent<unknown>) {
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
    }

    function handleOmniProductLevelChange(e: ByzzerChangeEvent<unknown>) {
        onContextChange?.('omniFocusProducts', [], []);
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
    }

    function handleCategoriesChange(e: ByzzerChangeEvent<string[]>) {
        if (!e.value.length) {
            onContextChange?.('characteristics', [], []);
        }
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
    }

    function handleRemoveUnavailableChars(unavailableCharacteristics: CharacteristicCondition[]) {
        // Filter available chars by removing those that match the unavailable characteristics
        const availableChars = internalValue.characteristics?.filter((char) =>
            !unavailableCharacteristics.some((unavailable) => unavailable.characteristic === char.characteristic)
        );

        onContextChange?.('characteristics', availableChars)
    }

    function handleOmniShopperSubCategoryChange(e: ByzzerChangeEvent<string[] | null>) {
        onContextChange?.('characteristics', [], []);
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value ?? [], e.data);
    }

    useImperativeHandle(ref, () => ({
        get value() {
            return internalValue;
        },
    }));

    useEffect(() => {
        setInternalValue(contextValue ?? {});
    }, [contextValue]);

    useEffect(() => {
        if (value) {
            setInternalValue(value);
        }
    }, [value]);

    useEffect(() => {
        const { brands = [], categories = [] } = internalValue;
        const requiresCheck = Boolean(
            brands.length && categories.length && categoriesToCheckForIntersect.length && includeFocusBrands && includeCategories
        );
        const missingIntersection =
            requiresCheck &&
            !internalValue.categories?.some((category) => categoriesToCheckForIntersect.includes(category));
        if (missingIntersection) {
            showMessage({
                type: 'warning',
                content: MESSAGE_NO_PREFERENCE_CATEGORY_MATCH
            });
        } else {
            hideMessage();
        }
    }, [categoriesToCheckForIntersect, internalValue.categories]);

    return (
        <FilterGroup className={classnames(baseClassName, className)}>
            {summary && <div className={`${baseClassName}__summary`}>{summary}</div>}
            
            {/* RMS and CPS */}
            <FilterSection
                onlyRenderIf={
                    (selectedDataset === 'rms' || selectedDataset === 'cps') &&
                    (includeFocusBrands || includeCategories)
                }
            >
                <ByzzerBrandSearch
                    onlyRenderIf={includeFocusBrands}
                    name={'brands'}
                    maxSelections={maxFocusBrands}
                    value={internalValue.brands}
                    onChange={handleChange}
                    label={labelsAndTips?.focusBrands ?? 'Focus Brand(s)'}
                />
                <ByzzerCategorySelect
                    name={'categories'}
                    onlyRenderIf={includeCategories}
                    value={internalValue.categories}
                    onChange={handleCategoriesChange}
                    placeholder={'Select from the list'}
                    allowClear={true}
                    categoriesToCheckForIntersect={categoriesToCheckForIntersect}
                    label={labelsAndTips?.categories ?? 'Category(s)'}
                    shouldDisplayIntersectIndicators={Boolean(categoriesToCheckForIntersect?.length)}
                    maxSelections={maxCategories}
                    groupOptionsByBrandCoverage={Boolean(categoriesToCheckForIntersect?.length)}
                />
                <SubCategorySelect
                    categories={internalValue.categories}
                    onChange={handleChange}
                    onlyRenderIf={includeProductSubcategory}
                    disabled={!internalValue.categories?.length}
                    placeholder={'All'}
                    label={
                        <>
                            <SelectorLabelInfo
                                selectorCode={'productSubCategory'}
                                required={requireProductSubcategory}
                                includeSuffix={!requireProductSubcategory}
                                sku={sku as string}
                            />
                        </>
                    }
                    value={internalValue.subcategories}
                    name={'subcategories'}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={(selectedDataset === 'rms' || selectedDataset === 'cps') && includeCharacteristics}>
                <label><b>Characteristic(s)</b></label>
                <CharacteristicCriteriaBuilder
                    name={'characteristics'}
                    value={internalValue.characteristics}
                    categories={internalValue.categories}
                    maxConditions={maxCharacteristics}
                    onChange={handleChange}
                    selectAllListedValuesEnabled={selectAllListedValuesEnabled}
                    includeUpcOption={true} // should eventually be set in Alby
                />
            </FilterSection>

            {/* Omnisales */}
            <FilterSection onlyRenderIf={selectedDataset === 'omni' && (includeOmniCategories || includeOmniProducts)}>
                <OmniCategorySelect
                    name={'categories'}
                    onlyRenderIf={includeOmniCategories}
                    value={internalValue.categories}
                    onChange={handleChange}
                    label={'Category(s)'}
                    placeholder={'Select from the list'}
                    maxSelections={maxCategories}
                />
                <ByzzerSelect
                    label={'Product Level'}
                    name={'omniProductLevel'}
                    options={omniProductLevelOptions}
                    value={internalValue.omniProductLevel}
                    onChange={handleOmniProductLevelChange}
                    placeholder={'Select a product level'}
                    disabled={!internalValue.categories?.length}
                    allowClear={true}
                />
                <OmniBrandSearch
                    name={'omniFocusProducts'}
                    onlyRenderIf={includeOmniProducts}
                    value={internalValue.omniFocusProducts}
                    onChange={handleChange}
                    label={'Focus Brand(s)'}
                    maxBrands={maxOmniProducts}
                    placeholder={'Search for brands'}
                    disabled={!internalValue.omniProductLevel || !internalValue.categories?.length}
                    omniCategories={internalValue.categories!}
                />
            </FilterSection>

            {/* Omnishopper */}
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperCategories}>
                <ByzzerOshCategorySelect
                    value={internalValue?.categories}
                    onChange={handleChange}
                    name={'categories'}
                    maxSelections={maxCategories}
                    label={'Category(s)'}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperSubCategories}>
                <ByzzerOshSubCategorySelect
                    onlyRenderIf={includeOmniShopperSubCategories}
                    categories={internalValue.categories}
                    value={internalValue?.subcategories}
                    onChange={handleOmniShopperSubCategoryChange}
                    name={'subcategories'}
                    disabled={!internalValue.categories?.length}
                    maxSelections={maxSubCategories}
                    label={'Select your subcategory'}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperProductDimensions}>
                <ByzzerOshCharacteristicsDimensionSelect
                    name={'productDimensions'}
                    value={internalValue?.productDimensions || []}
                    onChange={handleChange}
                    required={false}
                    label={'Product Dimension(s)'}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperDemographics}>
                <ByzzerOshDemographicCriteriaBuilder
                    name={'demographics'}
                    onChange={handleChange}
                    value={internalValue.demographics}
                    maxConditions={maxDemographicConditions ?? 1}
                    maxAllowedValues={maxDemographicValue ?? 1}
                    valueLabel={'Demographic Value'}
                    showConditionActions={false}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperCharacteristics}>
                <label><b>Characteristic(s)</b></label>
                <ByzzerOshCharacteristicBuilder
                    name={'characteristics'}
                    value={internalValue.characteristics}
                    categories={internalValue.categories}
                    subcategories={internalValue.subcategories}
                    maxAllowedValue={maxCharacteristicValues}
                    onChange={handleChange}
                    onRemoveUnavailableChars={handleRemoveUnavailableChars}
                    isProductDimensionsRequired={includeOmniShopperProductDimensions || false}
                    maxConditions={maxCharacteristics}
                    productDimensions={internalValue.productDimensions}
                    selectAllListedValuesEnabled={selectAllListedValuesEnabled}
                />
            </FilterSection>
        </FilterGroup>
    );

});

export default ProductFilters;

ProductFilters.displayName = 'ProductFilters';
